import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';

/** >= this width: two dashboard widgets per row (desktop / tablet landscape). */
const WIDGET_TWO_COLUMN_MIN_WIDTH = 768;

function layoutDashboardWidgets(nodes, isTwoColumn, styles) {
  if (!isTwoColumn) return nodes;
  const rows = [];
  for (let i = 0; i < nodes.length; i += 2) {
    rows.push(
      <View key={`widget-row-${i}`} style={styles.widgetRow}>
        <View style={styles.widgetHalf}>{nodes[i]}</View>
        {i + 1 < nodes.length ? (
          <View style={styles.widgetHalf}>{nodes[i + 1]}</View>
        ) : (
          <View style={styles.widgetHalf} />
        )}
      </View>
    );
  }
  return rows;
}

const EMPTY_INSIGHT = {
  student: { courses: [] },
  nextGrade: null,
  breaksRemainingNextGrade: 0,
  breaksTodoNextGrade: [],
  coursesThisWeek: [],
  avgDistinctCoursesPerSession7d: 0,
  trainedThisWeek: [],
  missedThisWeekUsual: [],
  stale90DaysPersonal: [],
  clubStale90Days: [],
};

const VIEW_MODES = ['number', 'bar', 'horizontalBar', 'pie', 'gauge', 'list'];

const MODE_LABELS = {
  number: 'Number',
  bar: 'Bar',
  horizontalBar: 'Horizontal',
  pie: 'Pie',
  gauge: 'Gauge',
  list: 'List',
};

function nextMode(current) {
  const i = VIEW_MODES.indexOf(current);
  return VIEW_MODES[(i + 1) % VIEW_MODES.length];
}

function sumValues(series) {
  return series.reduce((a, b) => a + (Number(b.value) || 0), 0);
}

function ChartNumber({ value, subtitle, theme, styles }) {
  return (
    <View style={styles.chartNumberWrap}>
      <Text style={[styles.chartNumberBig, { color: theme.colors.heading }]}>{value}</Text>
      {subtitle ? (
        <Text style={[styles.chartNumberSub, { color: theme.colors.text, opacity: 0.75 }]}>
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

function ChartBars({ series, horizontal, theme, styles }) {
  const max = Math.max(1, ...series.map((s) => Number(s.value) || 0));
  const colors = ['#d4af37', '#6b9bd1', '#7cb87c', '#c97b9e', '#a8a8a8', '#e6a23c'];
  if (!series.length) {
    return (
      <Text style={[styles.emptyChart, { color: theme.colors.text }]}>
        No data for this period — charts will fill in once you log sessions.
      </Text>
    );
  }
  if (horizontal) {
    return (
      <View style={styles.hBarWrap}>
        {series.map((s, idx) => {
          const v = Number(s.value) || 0;
          const pct = Math.round((v / max) * 100);
          return (
            <View key={`${s.label}-${idx}`} style={styles.hBarRow}>
              <Text
                style={[styles.hBarLabel, { color: theme.colors.text }]}
                numberOfLines={1}
              >
                {s.label}
              </Text>
              <View style={[styles.hBarTrack, { backgroundColor: theme.colors.border }]}>
                <View
                  style={[
                    styles.hBarFill,
                    { width: `${pct}%`, backgroundColor: colors[idx % colors.length] },
                  ]}
                />
              </View>
              <Text style={[styles.hBarVal, { color: theme.colors.heading }]}>{v}</Text>
            </View>
          );
        })}
      </View>
    );
  }
  return (
    <View style={styles.vBarWrap}>
      {series.map((s, idx) => {
        const v = Number(s.value) || 0;
        const pct = Math.round((v / max) * 100);
        return (
          <View key={`${s.label}-${idx}`} style={styles.vBarCol}>
            <Text style={[styles.vBarVal, { color: theme.colors.heading }]}>{v}</Text>
            <View style={[styles.vBarTrack, { backgroundColor: theme.colors.border }]}>
              <View
                style={[
                  styles.vBarFill,
                  { height: `${pct}%`, backgroundColor: colors[idx % colors.length] },
                ]}
              />
            </View>
            <Text
              style={[styles.vBarLabel, { color: theme.colors.text }]}
              numberOfLines={2}
            >
              {s.label}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function ChartPie({ series, theme, styles }) {
  const total = sumValues(series);
  const colors = ['#d4af37', '#6b9bd1', '#7cb87c', '#c97b9e', '#a8a8a8', '#e6a23c'];
  if (total <= 0) {
    return (
      <Text style={[styles.emptyChart, { color: theme.colors.text }]}>
        No data for this period — charts will fill in once you log sessions.
      </Text>
    );
  }
  return (
    <View style={styles.pieRow}>
      {series.map((s, idx) => {
        const v = Number(s.value) || 0;
        if (v <= 0) return null;
        const flex = v / total;
        return (
          <View
            key={`${s.label}-${idx}`}
            style={[styles.pieSeg, { flex, backgroundColor: colors[idx % colors.length] }]}
          />
        );
      })}
    </View>
  );
}

function ChartGauge({ value, max, label, theme, styles }) {
  const m = Math.max(1, max || 1);
  const v = Number(value) || 0;
  const fillPct = Math.min(100, Math.round((v / m) * 100));
  return (
    <View style={styles.gaugeWrap}>
      <Text style={[styles.gaugeLabel, { color: theme.colors.text }]}>{label}</Text>
      <View style={[styles.gaugeTrack, { backgroundColor: theme.colors.border }]}>
        <View
          style={[styles.gaugeFill, { width: `${fillPct}%`, backgroundColor: theme.colors.heading }]}
        />
      </View>
      <Text style={[styles.gaugePct, { color: theme.colors.heading }]}>
        {fillPct}% of scale ({v} / {m})
      </Text>
    </View>
  );
}

function ChartList({ lines, theme, styles }) {
  if (!lines.length) {
    return (
      <Text style={[styles.emptyChart, { color: theme.colors.text }]}>No rows yet.</Text>
    );
  }
  return (
    <View style={styles.listWrap}>
      {lines.map((line, i) => (
        <Text key={i} style={[styles.listLine, { color: theme.colors.text }]}>
          {line}
        </Text>
      ))}
    </View>
  );
}

function ReportWidget({
  title,
  mode,
  onCycleMode,
  theme,
  styles,
  children,
  containerStyle,
}) {
  return (
    <View
      style={[
        styles.widgetCard,
        {
          borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.2)' : '#ccc',
          backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fafafa',
        },
        containerStyle,
      ]}
    >
      <Text style={[styles.widgetTitle, { color: theme.colors.heading }]}>{title}</Text>
      <View style={styles.widgetBody}>{children}</View>
      <TouchableOpacity
        style={styles.widgetModeBtn}
        onPress={onCycleMode}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Text style={[styles.widgetModeBtnText, { color: theme.colors.text }]}>
          {MODE_LABELS[mode] || mode} ↻
        </Text>
      </TouchableOpacity>
    </View>
  );
}

export default function WhatShouldITrainPage({ navigation, route }) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isTwoColumn = windowWidth >= WIDGET_TWO_COLUMN_MIN_WIDTH;
  const stopwatchRef = React.useRef(null);
  const user = route?.params?.user;
  const userId = user?.id || route?.params?.userId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insight, setInsight] = useState(EMPTY_INSIGHT);
  const [modes, setModes] = useState({
    w1: 'number',
    w2: 'bar',
    w3: 'number',
    w4: 'list',
    w5: 'list',
    w6: 'list',
    w7: 'list',
    w8: 'list',
  });

  const [coursesCatalog, setCoursesCatalog] = useState([]);
  const [coursePickerOpen, setCoursePickerOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [courseBreaks, setCourseBreaks] = useState([]);
  const [loadingBreaks, setLoadingBreaks] = useState(false);

  const styles = useMemo(() => getStyles(theme), [theme]);

  const loadInsights = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/students/${userId}/training-insights`);
      if (!res.ok) throw new Error('Could not load training insights');
      const data = await res.json();
      setInsight(data);
      const cr = await fetch(`${getApiUrl()}/api/courses`);
      if (cr.ok) {
        const all = await cr.json();
        setCoursesCatalog(Array.isArray(all) ? dedupeCoursesForDropdown(all) : []);
      }
    } catch (e) {
      setInsight(EMPTY_INSIGHT);
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const enrolledCourseOptions = useMemo(() => {
    const effective = insight || EMPTY_INSIGHT;
    const names = effective.student?.courses || [];
    return coursesCatalog.filter((c) => names.includes(c.course_name));
  }, [insight, coursesCatalog]);

  const fetchCourseBreaks = useCallback(async () => {
    if (!userId || !selectedCourseId) {
      setCourseBreaks([]);
      return;
    }
    setLoadingBreaks(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/students/${userId}/training-insights/course-breaks?courseId=${selectedCourseId}`
      );
      if (!res.ok) throw new Error('breaks');
      const data = await res.json();
      setCourseBreaks(Array.isArray(data.items) ? data.items : []);
    } catch {
      setCourseBreaks([]);
    } finally {
      setLoadingBreaks(false);
    }
  }, [userId, selectedCourseId]);

  useEffect(() => {
    fetchCourseBreaks();
  }, [fetchCourseBreaks]);

  const setMode = (key, m) => setModes((prev) => ({ ...prev, [key]: m }));
  const cycle = (key) => setMode(key, nextMode(modes[key]));

  const displayInsight = insight || EMPTY_INSIGHT;

  const renderWidgetBody = (key, series, numberValue, numberSubtitle, listLines, gaugeMax) => {
    const mode = modes[key];
    if (mode === 'number') {
      return (
        <ChartNumber
          value={numberValue}
          subtitle={numberSubtitle}
          theme={theme}
          styles={styles}
        />
      );
    }
    if (mode === 'bar') {
      return <ChartBars series={series} horizontal={false} theme={theme} styles={styles} />;
    }
    if (mode === 'horizontalBar') {
      return <ChartBars series={series} horizontal theme={theme} styles={styles} />;
    }
    if (mode === 'pie') {
      return <ChartPie series={series} theme={theme} styles={styles} />;
    }
    if (mode === 'gauge') {
      return (
        <ChartGauge
          value={numberValue}
          max={gaugeMax}
          label={numberSubtitle || ''}
          theme={theme}
          styles={styles}
        />
      );
    }
    return <ChartList lines={listLines} theme={theme} styles={styles} />;
  };

  if (!userId) {
    return (
      <View style={[styles.screen, { backgroundColor: theme.colors.background }]}>
        <Text style={{ color: theme.colors.text }}>Missing user. Go back and open from Home.</Text>
      </View>
    );
  }

  return (
    <View style={[styles.screen, { backgroundColor: theme.colors.background }]}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>What should I train?</Text>
        <ProfileIcon navigation={navigation} user={user} />
      </View>

      {loading ? (
        <ActivityIndicator size="small" color={theme.colors.heading} style={{ marginVertical: 12 }} />
      ) : null}
      {error ? (
        <Text style={[styles.loadWarning, { color: theme.colors.text }]}>
          {error} — showing empty dashboards below.
        </Text>
      ) : null}
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          isTwoColumn && styles.scrollContentWide,
        ]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={[styles.intro, { color: theme.colors.text, opacity: 0.85 }]}>
          Dashboards and reporting from your training logs. Tap ↻ on a card to change how it is
          displayed.
        </Text>

        {layoutDashboardWidgets(
          [
            <ReportWidget
              key="w1"
          title='How many breaks have I left for my next grade?'
          mode={modes.w1}
          onCycleMode={() => cycle('w1')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w1',
            [{ label: 'Remaining', value: displayInsight.breaksRemainingNextGrade ?? 0 }],
            String(displayInsight.breaksRemainingNextGrade ?? 0),
            displayInsight.nextGrade ? `Next grade: ${displayInsight.nextGrade}` : 'No next grade in list',
            [`${displayInsight.breaksRemainingNextGrade ?? 0} breaks remaining`],
            Math.max(5, (displayInsight.breaksRemainingNextGrade ?? 0) + 2)
          )}
        </ReportWidget>,

            <ReportWidget
              key="w2"
          title='What courses have I trained this week?'
          mode={modes.w2}
          onCycleMode={() => cycle('w2')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w2',
            (displayInsight.coursesThisWeek || []).map((r) => ({ label: r.label, value: r.value })),
            String(sumValues(displayInsight.coursesThisWeek || [])),
            'Sessions touching each course (7 days)',
            (displayInsight.coursesThisWeek || []).map((r) => `${r.label}: ${r.value} session(s)`),
            Math.max(5, sumValues(displayInsight.coursesThisWeek || []))
          )}
        </ReportWidget>,

            <ReportWidget
              key="w3"
          title='Average distinct courses per training session (last 7 days)'
          mode={modes.w3}
          onCycleMode={() => cycle('w3')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w3',
            [
              {
                label: 'Avg / session',
                value: Math.round((displayInsight.avgDistinctCoursesPerSession7d || 0) * 100) / 100,
              },
            ],
            String(Math.round((displayInsight.avgDistinctCoursesPerSession7d || 0) * 100) / 100),
            'Distinct courses per session (exercises mapped to a course)',
            [
              `Average: ${Math.round((displayInsight.avgDistinctCoursesPerSession7d || 0) * 100) / 100}`,
            ],
            5
          )}
        </ReportWidget>,

            <ReportWidget
              key="w4"
          title='What did I train this week?'
          mode={modes.w4}
          onCycleMode={() => cycle('w4')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w4',
            (() => {
              const byCourse = {};
              (displayInsight.trainedThisWeek || []).forEach((row) => {
                const k = row.course || 'Other';
                byCourse[k] = (byCourse[k] || 0) + (row.count || 0);
              });
              return Object.keys(byCourse).map((k) => ({ label: k, value: byCourse[k] }));
            })(),
            String((displayInsight.trainedThisWeek || []).length),
            'Exercise logs (7 days)',
            (displayInsight.trainedThisWeek || []).map(
              (r) => `${r.course || '?'} — ${r.exercise || '?'} × ${r.count}`
            ),
            Math.max(10, (displayInsight.trainedThisWeek || []).length)
          )}
        </ReportWidget>,

            <ReportWidget
              key="w5"
          title='What did I not train this week that I usually train?'
          mode={modes.w5}
          onCycleMode={() => cycle('w5')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w5',
            (displayInsight.missedThisWeekUsual || []).map((name, i) => ({
              label: name || `Item ${i}`,
              value: 1,
            })),
            String((displayInsight.missedThisWeekUsual || []).length),
            'Courses with 2+ sessions in 90d but none in 7d',
            (displayInsight.missedThisWeekUsual || []).map((n) => `• ${n}`),
            5
          )}
        </ReportWidget>,

            <ReportWidget
              key="w6"
          title='What have I not trained in 3 months? (my courses)'
          mode={modes.w6}
          onCycleMode={() => cycle('w6')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w6',
            (displayInsight.stale90DaysPersonal || []).map((name) => ({ label: name, value: 1 })),
            String((displayInsight.stale90DaysPersonal || []).length),
            'Enrolled courses with no session in 90 days',
            (displayInsight.stale90DaysPersonal || []).map((n) => `• ${n}`),
            5
          )}
        </ReportWidget>,

            <ReportWidget
              key="w7"
          title='What has the club not trained in the last 3+ months?'
          mode={modes.w7}
          onCycleMode={() => cycle('w7')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w7',
            (displayInsight.clubStale90Days || []).map((name) => ({ label: name, value: 1 })),
            String((displayInsight.clubStale90Days || []).length),
            'Course appears on some student profile but no club session logged',
            (displayInsight.clubStale90Days || []).map((n) => `• ${n}`),
            8
          )}
        </ReportWidget>,

            <ReportWidget
              key="w8"
          title='Breaks for next grade & for a course'
          mode={modes.w8}
          onCycleMode={() => cycle('w8')}
          theme={theme}
          styles={styles}
          containerStyle={isTwoColumn ? styles.widgetCardGrid : undefined}
        >
          {renderWidgetBody(
            'w8',
            [
              {
                label: 'Next grade todo',
                value: displayInsight.breaksTodoNextGrade?.length || 0,
              },
            ],
            String(displayInsight.breaksTodoNextGrade?.length || 0),
            displayInsight.nextGrade ? `Next grade: ${displayInsight.nextGrade}` : 'Next grade',
            (displayInsight.breaksTodoNextGrade || []).map((b) => `• ${b.break_name} (${b.status})`),
            Math.max(8, (displayInsight.breaksTodoNextGrade?.length || 0) + 2)
          )}
            <TouchableOpacity
              style={[styles.coursePickBtn, { borderColor: theme.colors.border }]}
              onPress={() => setCoursePickerOpen(true)}
            >
              <Text style={{ color: theme.colors.heading }}>
                Course for remaining breaks:{' '}
                {selectedCourseId
                  ? enrolledCourseOptions.find((c) => c.id === selectedCourseId)?.course_name ||
                    'Selected'
                  : 'Choose a course'}
              </Text>
            </TouchableOpacity>
            {loadingBreaks ? (
              <ActivityIndicator color={theme.colors.heading} style={{ marginTop: 8 }} />
            ) : (
              <ChartList
                lines={(courseBreaks || []).map((b) => `• ${b.break_name} (${b.status})`)}
                theme={theme}
                styles={styles}
              />
            )}
            <Text style={[styles.hint, { color: theme.colors.text }]}>
              Chart modes (↻) apply to the combined text summary above; use List for readable break
              names.
            </Text>
          </ReportWidget>,
          ],
          isTwoColumn,
          styles
        )}
      </ScrollView>

      <Modal visible={coursePickerOpen} transparent animationType="fade">
        <Pressable style={styles.modalBackdrop} onPress={() => setCoursePickerOpen(false)}>
          <Pressable style={[styles.modalSheet, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.heading }]}>Your courses</Text>
            <ScrollView style={{ maxHeight: 320 }}>
              {enrolledCourseOptions.map((c) => (
                <TouchableOpacity
                  key={c.id}
                  style={styles.modalRow}
                  onPress={() => {
                    setSelectedCourseId(c.id);
                    setCoursePickerOpen(false);
                  }}
                >
                  <Text style={{ color: theme.colors.text }}>{c.course_name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity
              style={styles.modalRow}
              onPress={() => {
                setSelectedCourseId(null);
                setCoursePickerOpen(false);
              }}
            >
              <Text style={{ color: theme.colors.text, opacity: 0.7 }}>Clear selection</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    screen: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingTop: Platform.OS === 'web' ? 16 : 48,
      paddingBottom: 12,
    },
    breadcrumb: { fontSize: 18, fontWeight: '700', flex: 1, textAlign: 'center' },
    scrollContent: { padding: 16, paddingBottom: 48 },
    scrollContentWide: {
      maxWidth: 1200,
      width: '100%',
      alignSelf: 'center',
      paddingHorizontal: 20,
    },
    intro: { marginBottom: 16, fontSize: 14, lineHeight: 20 },
    widgetRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 12,
      marginBottom: 16,
    },
    widgetHalf: {
      flex: 1,
      minWidth: 0,
    },
    widgetCard: {
      borderWidth: 1,
      borderRadius: 10,
      padding: 14,
      paddingBottom: 40,
      marginBottom: 16,
      position: 'relative',
    },
    widgetCardGrid: {
      marginBottom: 0,
      flex: 1,
      alignSelf: 'stretch',
    },
    widgetTitle: { fontSize: 15, fontWeight: '700', marginBottom: 10 },
    widgetBody: { minHeight: 100 },
    widgetModeBtn: {
      position: 'absolute',
      right: 10,
      bottom: 8,
      paddingVertical: 4,
      paddingHorizontal: 8,
    },
    widgetModeBtnText: { fontSize: 12, fontWeight: '600' },
    chartNumberWrap: { alignItems: 'center', justifyContent: 'center', paddingVertical: 12 },
    chartNumberBig: { fontSize: 44, fontWeight: '800' },
    chartNumberSub: { fontSize: 13, marginTop: 6, textAlign: 'center' },
    emptyChart: { padding: 12, fontSize: 14 },
    vBarWrap: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-around',
      height: 160,
      paddingTop: 8,
    },
    vBarCol: { flex: 1, alignItems: 'center', marginHorizontal: 2 },
    vBarVal: { fontSize: 11, marginBottom: 4 },
    vBarTrack: {
      width: '70%',
      height: 100,
      borderRadius: 4,
      justifyContent: 'flex-end',
      overflow: 'hidden',
    },
    vBarFill: { width: '100%', minHeight: 4, borderRadius: 4 },
    vBarLabel: { fontSize: 10, marginTop: 6, textAlign: 'center' },
    hBarWrap: { gap: 8 },
    hBarRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
    hBarLabel: { width: 100, fontSize: 12 },
    hBarTrack: { flex: 1, height: 14, borderRadius: 7, overflow: 'hidden', marginHorizontal: 8 },
    hBarFill: { height: '100%', borderRadius: 7 },
    hBarVal: { width: 36, fontSize: 12, textAlign: 'right' },
    pieRow: { flexDirection: 'row', height: 48, borderRadius: 8, overflow: 'hidden', marginTop: 8 },
    pieSeg: { minWidth: 4 },
    gaugeWrap: { paddingVertical: 8 },
    gaugeLabel: { fontSize: 13, marginBottom: 6 },
    gaugeTrack: { height: 16, borderRadius: 8, overflow: 'hidden' },
    gaugeFill: { height: '100%', borderRadius: 8 },
    gaugePct: { marginTop: 6, fontSize: 12 },
    listWrap: { paddingVertical: 4 },
    listLine: { fontSize: 14, marginBottom: 6, lineHeight: 20 },
    subHeading: { fontWeight: '600', marginBottom: 6, marginTop: 4 },
    coursePickBtn: {
      borderWidth: 1,
      padding: 10,
      borderRadius: 8,
      marginTop: 10,
      marginBottom: 8,
    },
    hint: { fontSize: 12, opacity: 0.7, marginTop: 8 },
    errorText: { padding: 20, textAlign: 'center' },
    loadWarning: {
      marginHorizontal: 16,
      marginBottom: 4,
      padding: 10,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.12)' : 'rgba(0,0,0,0.06)',
      fontSize: 13,
      lineHeight: 18,
    },
    modalBackdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      padding: 24,
    },
    modalSheet: { borderRadius: 12, padding: 16, maxHeight: '80%' },
    modalTitle: { fontSize: 17, fontWeight: '700', marginBottom: 12 },
    modalRow: { paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#8883' },
  });
}
